/**
 * Multi-metric time series chart using Nivo Line.
 * Supports multiple series, date range display, and AI-generated annotations.
 */

import { memo, useMemo } from 'react'
import { ResponsiveLine } from '@nivo/line'
import { useNivoTheme, useChartColors } from './nivoTheme'

export interface TimeSeriesDataPoint {
  date: string
  spend?: number
  impressions?: number
  clicks?: number
  conversations?: number
  leads?: number
  purchases?: number
  [metric: string]: string | number | undefined
}

interface PerformanceTrendsProps {
  data: TimeSeriesDataPoint[]
  metrics?: string[]
  height?: number
  highlights?: string[]
  yFormat?: 'number' | 'currency'
  enableArea?: boolean
}

const METRIC_LABELS: Record<string, string> = {
  spend: 'Spend',
  impressions: 'Impressions',
  clicks: 'Clicks',
  conversations: 'Conversations',
  leads: 'Leads',
  purchases: 'Purchases',
  ctr: 'CTR',
  cpc: 'CPC',
  roas: 'ROAS',
}

function formatValue(value: number, format: 'number' | 'currency'): string {
  if (format === 'currency') {
    return `$${(value / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return value.toLocaleString()
}

export const PerformanceTrends = memo(function PerformanceTrends({
  data,
  metrics = ['spend'],
  height = 300,
  highlights: _highlights,
  yFormat = 'number',
  enableArea = false,
}: PerformanceTrendsProps) {
  const theme = useNivoTheme()
  const chartColors = useChartColors()

  // Memoize series to avoid Nivo re-computing on every parent render
  const series = useMemo(() => {
    if (data.length === 0) return []
    return metrics.map((metric, idx) => ({
      id: METRIC_LABELS[metric] || metric,
      color: chartColors[idx % chartColors.length],
      data: data.map((point) => ({
        x: point.date,
        y: typeof point[metric] === 'number' ? point[metric] : 0,
      })),
    }))
  }, [data, metrics, chartColors])

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No time series data available
      </div>
    )
  }

  return (
    <div style={{ height }}>
      <ResponsiveLine
        data={series}
        theme={theme}
        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
        curve="monotoneX"
        colors={series.map((s) => s.color)}
        lineWidth={2}
        pointSize={data.length <= 14 ? 6 : 0}
        pointColor={{ from: 'color' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'seriesColor' }}
        enableArea={enableArea}
        areaOpacity={0.08}
        enableGridX={false}
        enableSlices="x"
        crosshairType="bottom"
        axisBottom={{
          tickRotation: data.length > 14 ? -45 : 0,
          tickSize: 5,
          tickPadding: 5,
          format: (v: string) => {
            // Show shorter date format for readability
            const parts = v.split('-')
            return parts.length === 3 ? `${parts[1]}/${parts[2]}` : v
          },
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          format: (v: number) => formatValue(v, yFormat),
        }}
        motionConfig="gentle"
        sliceTooltip={({ slice }) => (
          <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
            <p className="font-semibold mb-1 text-popover-foreground">
              {String(slice.points[0]?.data.x || '')}
            </p>
            {slice.points.map((point) => (
              <div key={point.id} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: point.seriesColor }}
                />
                <span className="text-muted-foreground">{point.seriesId}:</span>
                <span className="font-medium">{formatValue(Number(point.data.y), yFormat)}</span>
              </div>
            ))}
          </div>
        )}
        legends={metrics.length > 1 ? [{
          anchor: 'top-right' as const,
          direction: 'row' as const,
          justify: false,
          translateX: 0,
          translateY: -20,
          itemsSpacing: 16,
          itemDirection: 'left-to-right' as const,
          itemWidth: 80,
          itemHeight: 16,
          symbolSize: 10,
          symbolShape: 'circle' as const,
        }] : []}
      />
    </div>
  )
})
